import {
  cn,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@up2stay/up2promo-ui';
import { Check, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

/** Solid pill that opens a multi-select popover; turns brand-tinted with a count when active. */
export function FilterChipPopover({
  icon: Icon,
  label,
  count,
  children,
}: {
  icon: LucideIcon;
  label: string;
  count: number;
  children: ReactNode;
}) {
  const active = count > 0;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex h-[34px] shrink-0 items-center gap-1.5 rounded-full border px-3 text-[12.5px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            active
              ? 'border-[color:var(--ghost-accent-border)] bg-[color:var(--ghost-accent-bg)] font-medium text-[color:var(--ghost-accent-fg)]'
              : 'border-solid border-input bg-card text-muted-foreground hover:bg-muted/50 hover:text-foreground',
          )}
        >
          <Icon className="size-[13px]" aria-hidden />
          {label}
          {active ? (
            <span className="rounded-full bg-[color:var(--ghost-accent-fg)] px-1.5 text-[10.5px] font-semibold leading-4 text-white">
              {count}
            </span>
          ) : null}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="max-h-72 w-60 overflow-y-auto p-1.5"
      >
        {children}
      </PopoverContent>
    </Popover>
  );
}

export function FilterChipOption({
  selected,
  onToggle,
  children,
}: {
  selected: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="menuitemcheckbox"
      aria-checked={selected}
      onClick={onToggle}
      className="flex min-h-9 w-full items-center gap-2 rounded-md px-2.5 text-left text-[13px] hover:bg-muted/60"
    >
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {selected ? (
        <Check
          className="size-4 text-[color:var(--ghost-accent-fg)]"
          aria-hidden
        />
      ) : null}
    </button>
  );
}
